import {
  AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Ban, Bot, Box, Boxes, CircleDot, Cpu, Eye,
  Footprints, Handshake, History, Layers, MapPin, Package, PackageOpen, PackagePlus, PackageMinus,
  Navigation, PenLine, Pickaxe, Radio, Ship, Shuffle, Sparkles, SquareStack, Trash2, User, UserMinus, UserPlus, Users, Waypoints,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { LogbookActorKind, LogbookEvent, LogbookOutcome } from '../types/logbook';

/**
 * The Logbook action taxonomy, as the Logbook mod actually emits it.
 *
 * Each entry names the capture boundary that produced the action and, where the integration
 * documentation records one, the limit that stops the record short of proof. Those limits are not
 * decoration: an `observed` container slot change is not a transfer, a menu access is not theft, and
 * `docs/INTEGRATIONS.md` requires that gap stay visible wherever the evidence is shown.
 *
 * An action Argus has never seen still renders: it falls back to its namespace, so a new mod adapter
 * appears as readable evidence on the day it ships rather than waiting for a frontend release.
 */

export type LogbookTone = 'neutral' | 'accent' | 'purple' | 'success' | 'warning' | 'amber' | 'orange' | 'danger' | 'info';

export type LogbookCategory =
  | 'Blocks' | 'Containers' | 'Items' | 'Storage networks' | 'Land and teams'
  | 'Travel' | 'Ships and vehicles' | 'Creatures' | 'Trade' | 'Telemetry' | 'Evidence integrity';

export interface LogbookActionMeta {
  action: string;
  /** Short label for a badge. */
  label: string;
  /** Past-tense verb used to read a row as a sentence. */
  verb: string;
  /** The mod or subsystem whose hook produced this. */
  source: string;
  category: LogbookCategory;
  tone: LogbookTone;
  icon: ComponentType<{ className?: string }>;
  /** What this record does not establish. Shown wherever the event is read in full. */
  caveat?: string;
  /**
   * Kept so the action still renders, but not offered in the picker: an older or undocumented name
   * that the current server inventory does not emit.
   */
  legacy?: boolean;
}

const VANILLA_BREAK_LIMIT = 'Changes that bypass the vanilla break method are not intercepted. Drops and block entity contents are not reconstructed.';
const SLOT_LIMIT = 'At most one selected slot and the cursor were read. Shift-clicks, sorting, crafting and other slots are not treated as transfers.';

const NAMED_ACTIONS: LogbookActionMeta[] = [
  // Vanilla capture boundaries
  { action: 'block.break', label: 'Block break', verb: 'broke', source: 'Vanilla', category: 'Blocks', tone: 'danger', icon: Pickaxe, caveat: VANILLA_BREAK_LIMIT },
  { action: 'block.place', label: 'Block place', verb: 'placed', source: 'Vanilla', category: 'Blocks', tone: 'success', icon: Box, caveat: 'Non-block-item machinery, arbitrary world writes and multi-block side effects can bypass this hook.' },
  { action: 'sign.edit', label: 'Sign edit', verb: 'edited a sign at', source: 'Vanilla', category: 'Blocks', tone: 'accent', icon: PenLine, caveat: 'The four lines before and after the edit, per side, as the server stored them. Dye, glow, wax and chat filtering are not captured; an unchanged or refused edit is observed.' },
  { action: 'container.deposit', label: 'Deposit', verb: 'deposited', source: 'Vanilla', category: 'Containers', tone: 'success', icon: ArrowDownToLine, caveat: SLOT_LIMIT },
  { action: 'container.withdraw', label: 'Withdraw', verb: 'withdrew', source: 'Vanilla', category: 'Containers', tone: 'danger', icon: ArrowUpFromLine, caveat: SLOT_LIMIT },
  { action: 'container.slot_change', label: 'Slot change', verb: 'changed a slot on', source: 'Vanilla', category: 'Containers', tone: 'neutral', icon: Shuffle, caveat: `${SLOT_LIMIT} A slot change is an observation, not a proven transfer.` },
  { action: 'menu.open', label: 'Menu open', verb: 'opened', source: 'Vanilla', category: 'Containers', tone: 'info', icon: PackageOpen, caveat: 'Successful server menu access only. Opening a grave, shop or storage menu does not prove a transfer, ownership or theft.' },
  { action: 'merchant.trade', label: 'Villager trade', verb: 'traded with', source: 'Vanilla', category: 'Trade', tone: 'purple', icon: Handshake, caveat: 'Only the active offer and its two payment slots were read. This is transaction evidence, not inventory reconstruction.' },
  { action: 'inventory.slot_snapshot', label: 'Inventory snapshot', verb: 'had an inventory slot change', source: 'Vanilla', category: 'Items', tone: 'neutral', icon: SquareStack, caveat: 'A snapshot is not a deposit, a withdrawal or an accusation. No transfer source or destination is inferred.' },
  { action: 'item.drop', label: 'Item drop', verb: 'dropped', source: 'Vanilla', category: 'Items', tone: 'orange', icon: PackageMinus, caveat: 'A returned drop entity can still be cancelled or removed later. Later ownership is not inferred.' },
  { action: 'item.pickup', label: 'Item pickup', verb: 'picked up', source: 'Vanilla', category: 'Items', tone: 'success', icon: PackagePlus },
  { action: 'player.state', label: 'Position sample', verb: 'was seen at', source: 'Vanilla', category: 'Telemetry', tone: 'neutral', icon: Footprints, caveat: 'Positions are observations at most once per player per twenty ticks, not a complete movement replay.' },

  // Create
  { action: 'create.block_change', label: 'Create block change', verb: 'changed a block with Create at', source: 'Create', category: 'Blocks', tone: 'orange', icon: Cpu, caveat: 'Not a universal Create machine or contraption logger. Machine ownership is not player causality.' },

  // Applied Energistics 2
  { action: 'ae2.deposit', label: 'AE2 insert', verb: 'inserted into an ME network', source: 'Applied Energistics 2', category: 'Storage networks', tone: 'success', icon: Layers, caveat: 'Simulated and zero-result operations are omitted. The player position can be remote from the network storage.' },
  { action: 'ae2.withdraw', label: 'AE2 extract', verb: 'extracted from an ME network', source: 'Applied Energistics 2', category: 'Storage networks', tone: 'danger', icon: Layers, caveat: 'Simulated and zero-result operations are omitted. The player position can be remote from the network storage.' },

  // Refined Storage 1 and 2
  { action: 'refinedstorage.deposit', label: 'RS insert', verb: 'inserted into a storage network', source: 'Refined Storage', category: 'Storage networks', tone: 'success', icon: Boxes, caveat: 'The initiator is unavailable from this API: the network controller location is recorded instead, and the actor stays unknown.' },
  { action: 'refinedstorage.withdraw', label: 'RS extract', verb: 'extracted from a storage network', source: 'Refined Storage', category: 'Storage networks', tone: 'danger', icon: Boxes, caveat: 'The initiator is unavailable from this API: the network controller location is recorded instead, and the actor stays unknown.' },

  // Carry On
  { action: 'carryon.block_pickup', label: 'Carried block up', verb: 'picked up a block with Carry On', source: 'Carry On', category: 'Blocks', tone: 'orange', icon: Package, caveat: 'Handler acceptance without an observed block change is only observed. Carried inventories and NBT are not enumerated.' },
  { action: 'carryon.block_place', label: 'Carried block down', verb: 'placed a carried block', source: 'Carry On', category: 'Blocks', tone: 'success', icon: Package, caveat: 'Candidate positions are inspected; wider secondary changes are not enumerated.' },
  { action: 'carryon.entity_pickup', label: 'Carried entity', verb: 'picked up an entity with Carry On', source: 'Carry On', category: 'Creatures', tone: 'orange', icon: Package },

  // FTB Chunks
  { action: 'ftbchunks.claim', label: 'Chunk claimed', verb: 'claimed a chunk at', source: 'FTB Chunks', category: 'Land and teams', tone: 'success', icon: MapPin, caveat: 'Chunk minimum coordinates are scope markers, not a position. Team ownership is separate from the actor.' },
  { action: 'ftbchunks.unclaim', label: 'Chunk unclaimed', verb: 'unclaimed a chunk at', source: 'FTB Chunks', category: 'Land and teams', tone: 'danger', icon: MapPin, caveat: 'Chunk minimum coordinates are scope markers, not a position. Team ownership is separate from the actor.' },
  { action: 'ftbchunks.load', label: 'Chunk force-loaded', verb: 'force-loaded a chunk at', source: 'FTB Chunks', category: 'Land and teams', tone: 'info', icon: Radio },
  { action: 'ftbchunks.unload', label: 'Chunk unloaded', verb: 'stopped force-loading a chunk at', source: 'FTB Chunks', category: 'Land and teams', tone: 'neutral', icon: Radio },

  // FTB Teams
  { action: 'ftbteams.ownership_transferred', label: 'Team owner changed', verb: 'is recorded on a team ownership transfer', source: 'FTB Teams', category: 'Land and teams', tone: 'warning', icon: Users, caveat: 'Public notifications do not establish an initiator, so the actor stays unknown and the outcome is observed.' },
  { action: 'ftbteams.properties_changed', label: 'Team settings', verb: 'is recorded on a team property change', source: 'FTB Teams', category: 'Land and teams', tone: 'neutral', icon: Users, caveat: 'Property collections are not serialized, and the notification does not establish an initiator.' },
  { action: 'ftbteams.player_changed', label: 'Team members', verb: 'is recorded on a team member change', source: 'FTB Teams', category: 'Land and teams', tone: 'info', icon: Users, caveat: 'At most eight affected member UUIDs are copied. The notification does not establish an initiator.' },
  { action: 'ftbteams.add_ally', label: 'Ally added', verb: 'is recorded on a team ally being added', source: 'FTB Teams', category: 'Land and teams', tone: 'success', icon: UserPlus },
  { action: 'ftbteams.remove_ally', label: 'Ally removed', verb: 'is recorded on a team ally being removed', source: 'FTB Teams', category: 'Land and teams', tone: 'orange', icon: UserMinus },
  { action: 'ftbteams.deleted', label: 'Team deleted', verb: 'is recorded on a team deletion', source: 'FTB Teams', category: 'Land and teams', tone: 'danger', icon: Trash2, caveat: 'Public notifications do not establish an initiator, so the actor stays unknown.' },

  // Open Parties and Claims
  { action: 'openpac.claim_change', label: 'Claim change', verb: 'is recorded on a claim change at', source: 'Open Parties and Claims', category: 'Land and teams', tone: 'info', icon: MapPin, caveat: 'The initiator is unavailable. Scope notifications are observations, not attributed player actions.' },
  { action: 'openpac.scope_change', label: 'Claim scope change', verb: 'is recorded on a claim scope change', source: 'Open Parties and Claims', category: 'Land and teams', tone: 'neutral', icon: MapPin, caveat: 'Region and dimension notifications use placeholder coordinates. The initiator is unavailable.' },

  // Waystones
  { action: 'waystones.teleport', label: 'Waystone teleport', verb: 'teleported to', source: 'Waystones', category: 'Travel', tone: 'purple', icon: Waypoints, caveat: 'The recorded target can differ from the landing position. Modern teleport requirements are not enumerated as a cost.' },
  { action: 'waystones.activate', label: 'Waystone activated', verb: 'activated a waystone at', source: 'Waystones', category: 'Travel', tone: 'accent', icon: Waypoints },

  // Cobblemon
  { action: 'cobblemon.capture', label: 'Pokemon captured', verb: 'captured', source: 'Cobblemon', category: 'Creatures', tone: 'success', icon: Sparkles, caveat: 'Party, PC and NBT contents are never walked. Only the Pokemon UUID and species are retained.' },
  { action: 'cobblemon.release', label: 'Pokemon released', verb: 'released', source: 'Cobblemon', category: 'Creatures', tone: 'danger', icon: Sparkles, caveat: 'Party, PC and NBT contents are never walked. Only the Pokemon UUID and species are retained.' },
  { action: 'cobblemon.trade_participation', label: 'Pokemon trade', verb: 'took part in a trade of', source: 'Cobblemon', category: 'Trade', tone: 'purple', icon: Handshake, caveat: 'One trade produces two participant records sharing a trade_correlation. Group them before counting trades.' },

  // Logbook itself
  { action: 'logbook.gap', label: 'Evidence gap', verb: 'could not be captured near', source: 'Logbook', category: 'Evidence integrity', tone: 'warning', icon: AlertTriangle, caveat: 'Capture failed here. Evidence for this interval is incomplete, and area rewind refuses intervals containing a gap.' },
  { action: 'logbook.access', label: 'Evidence access', verb: 'read evidence', source: 'Logbook', category: 'Evidence integrity', tone: 'neutral', icon: Eye },
  { action: 'rewind.block', label: 'Rewind block step', verb: 'reverted a block at', source: 'Logbook', category: 'Evidence integrity', tone: 'purple', icon: History },

  // Ship and vehicle lifecycle. Assembly, movement and removal are about the craft itself; the
  // blocks on it break and place through the per-integration hooks generated below.
  { action: 'sable.ship_add', label: 'Ship created', verb: 'created a ship at', source: 'Sable', category: 'Ships and vehicles', tone: 'success', icon: Ship },
  { action: 'sable.ship_remove', label: 'Ship removed', verb: 'removed a ship at', source: 'Sable', category: 'Ships and vehicles', tone: 'danger', icon: Ship },
  { action: 'sable.ship_move', label: 'Ship moved', verb: 'moved a ship to', source: 'Sable', category: 'Ships and vehicles', tone: 'accent', icon: Navigation, caveat: 'A recorded position is where the ship was observed, not a complete path.' },
  { action: 'sable.ship_loaded', label: 'Ship loaded', verb: 'is recorded on a ship loading at', source: 'Sable', category: 'Ships and vehicles', tone: 'info', icon: Ship },
  { action: 'sable.ship_unload', label: 'Ship unloaded', verb: 'is recorded on a ship unloading at', source: 'Sable', category: 'Ships and vehicles', tone: 'neutral', icon: Ship },
  { action: 'sable.ship_rewind', label: 'Ship rewind', verb: 'rewound a ship at', source: 'Sable', category: 'Ships and vehicles', tone: 'purple', icon: History },
  { action: 'sable.block_change', label: 'Ship block change', verb: 'changed a ship block at', source: 'Sable', category: 'Blocks', tone: 'orange', icon: Ship },
  { action: 'simulated.ship_assemble', label: 'Ship assembled', verb: 'assembled a ship at', source: 'Simulated', category: 'Ships and vehicles', tone: 'success', icon: Ship },
  { action: 'simulated.ship_disassemble', label: 'Ship disassembled', verb: 'disassembled a ship at', source: 'Simulated', category: 'Ships and vehicles', tone: 'danger', icon: Ship },

  // Names seen in older evidence. Not offered in the picker, but still readable when encountered.
  { action: 'inventory.transfer', label: 'Inventory transfer', verb: 'moved', source: 'Vanilla', category: 'Items', tone: 'info', icon: Shuffle, legacy: true },
  { action: 'carryon.pickup', label: 'Carried pickup', verb: 'picked up with Carry On', source: 'Carry On', category: 'Blocks', tone: 'orange', icon: Package, legacy: true },
  { action: 'carryon.place', label: 'Carried place', verb: 'placed with Carry On', source: 'Carry On', category: 'Blocks', tone: 'success', icon: Package, legacy: true },
  { action: 'rewind.apply', label: 'Rewind applied', verb: 'applied an area rewind at', source: 'Logbook', category: 'Evidence integrity', tone: 'purple', icon: History, legacy: true, caveat: 'Rewind restores supported block state history. Container contents, entities and inventories are not reconstructed.' },
];

/**
 * Integrations that capture a plain block break and place at their own boundary.
 *
 * Every one of these emits `<namespace>.block_break` and `<namespace>.block_place`, so the entries
 * are generated rather than typed out fifty times. The list is the hinge: adding a mod here is the
 * whole change needed when the server gains one.
 */
const BLOCK_INTEGRATIONS: { namespace: string; source: string }[] = [
  { namespace: 'create', source: 'Create' },
  { namespace: 'createbigcannons', source: 'Create: Big Cannons' },
  { namespace: 'copycats', source: 'Copycats+' },
  { namespace: 'railways', source: "Create: Steam 'n' Rails" },
  { namespace: 'createaddition', source: 'Create Crafts & Additions' },
  { namespace: 'create_connected', source: 'Create: Connected' },
  { namespace: 'createdeco', source: 'Create Deco' },
  { namespace: 'create_enchantment_industry', source: 'Create: Enchantment Industry' },
  { namespace: 'sliceanddice', source: 'Slice & Dice' },
  { namespace: 'createstuffadditions', source: 'Create Stuff & Additions' },
  { namespace: 'create_new_age', source: 'Create: New Age' },
  { namespace: 'vintageimprovements', source: 'Vintage Improvements' },
  { namespace: 'tfmg', source: 'Create: The Factory Must Grow' },
  { namespace: 'numismatics', source: 'Numismatics' },
  { namespace: 'bellsandwhistles', source: 'Create: Bells & Whistles' },
  { namespace: 'sophisticatedstorage', source: 'Sophisticated Storage' },
  { namespace: 'sophisticatedbackpacks', source: 'Sophisticated Backpacks' },
  { namespace: 'storagedrawers', source: 'Storage Drawers' },
  { namespace: 'toms_storage', source: "Tom's Simple Storage" },
  { namespace: 'sable', source: 'Sable' },
  { namespace: 'aeronautics', source: 'Aeronautics' },
  { namespace: 'simulated', source: 'Simulated' },
  { namespace: 'offroad', source: 'Offroad' },
];

const BLOCK_HOOK_LIMIT = 'Captured at this integration\'s own boundary. A change that bypasses that hook is not intercepted, and drops or block entity contents are not reconstructed.';

const GENERATED_BLOCK_ACTIONS: LogbookActionMeta[] = BLOCK_INTEGRATIONS.flatMap(({ namespace, source }) => [
  {
    action: `${namespace}.block_break`, label: 'Block break', verb: 'broke', source,
    category: 'Blocks' as const, tone: 'danger' as const, icon: Pickaxe, caveat: BLOCK_HOOK_LIMIT,
  },
  {
    action: `${namespace}.block_place`, label: 'Block place', verb: 'placed', source,
    category: 'Blocks' as const, tone: 'success' as const, icon: Box, caveat: BLOCK_HOOK_LIMIT,
  },
]);

const ACTIONS: LogbookActionMeta[] = [...NAMED_ACTIONS, ...GENERATED_BLOCK_ACTIONS];

const ACTION_INDEX = new Map(ACTIONS.map((meta) => [meta.action, meta]));

// A duplicate would make the index depend on array order, so it is a build-time mistake, not a
// runtime preference. `create.block_break` exists once, from the generated family, and so does
// everything else.
if (ACTION_INDEX.size !== ACTIONS.length) {
  throw new Error('the Logbook action taxonomy contains a duplicate action');
}

/** Actions grouped for the filter picker, in the order a moderator tends to reach for them. */
export const LOGBOOK_CATEGORY_ORDER: LogbookCategory[] = [
  'Blocks', 'Containers', 'Items', 'Storage networks', 'Land and teams',
  'Travel', 'Ships and vehicles', 'Creatures', 'Trade', 'Telemetry', 'Evidence integrity',
];

export const LOGBOOK_ACTIONS = ACTIONS;

/**
 * Loglibrarian accepts at most 32 exact actions in one filter. There are more than fifty block
 * actions alone, so "every block change" is not expressible as a query: that question is asked by
 * leaving actions empty and narrowing with a region and a window instead.
 */
export const LOGBOOK_ACTION_LIMIT = 32;

export function actionsByCategory(): { category: LogbookCategory; actions: LogbookActionMeta[] }[] {
  return LOGBOOK_CATEGORY_ORDER
    .map((category) => ({ category, actions: ACTIONS.filter((meta) => meta.category === category && !meta.legacy) }))
    .filter((group) => group.actions.length > 0);
}

/** Every break and place emitted by the named integrations, for the preset bundles below. */
const blocksFor = (namespaces: string[]): string[] =>
  namespaces.flatMap((namespace) => [`${namespace}.block_break`, `${namespace}.block_place`]);

const CREATE_FAMILY = [
  'create', 'createbigcannons', 'copycats', 'railways', 'createaddition', 'create_connected',
  'createdeco', 'create_enchantment_industry', 'sliceanddice', 'createstuffadditions',
  'create_new_age', 'vintageimprovements', 'tfmg', 'numismatics', 'bellsandwhistles',
];
const STORAGE_MODS = ['sophisticatedstorage', 'sophisticatedbackpacks', 'storagedrawers', 'toms_storage'];
const VEHICLE_MODS = ['sable', 'aeronautics', 'simulated', 'offroad'];

/**
 * Preset bundles for the question a moderator is actually asking.
 *
 * Each one fits inside the 32 action ceiling, which is why they are scoped by integration family
 * rather than offering a single "everything" button that could not be sent. A preset is a starting
 * point, never a claim to be exhaustive.
 */
export const LOGBOOK_PRESETS: { id: string; label: string; description: string; actions: string[]; icon: ComponentType<{ className?: string }> }[] = [
  {
    id: 'grief',
    label: 'Griefing',
    description: 'Vanilla, Carry On and ship block changes. The everyday griefing question.',
    icon: Pickaxe,
    actions: ['block.break', 'block.place', 'sign.edit', 'create.block_change', 'sable.block_change', 'carryon.block_pickup', 'carryon.block_place'],
  },
  {
    id: 'create',
    label: 'Create family',
    description: 'Break and place captured by Create and every Create addon on the pack.',
    icon: Cpu,
    actions: ['create.block_change', ...blocksFor(CREATE_FAMILY)],
  },
  {
    id: 'theft',
    label: 'Item movement',
    description: 'Container and storage network transfers, plus the menu accesses and trades around them.',
    icon: PackageOpen,
    actions: [
      'container.withdraw', 'container.deposit', 'container.slot_change', 'menu.open',
      'ae2.withdraw', 'ae2.deposit', 'refinedstorage.withdraw', 'refinedstorage.deposit',
      'item.drop', 'item.pickup', 'inventory.slot_snapshot', 'merchant.trade',
    ],
  },
  {
    id: 'storage',
    label: 'Storage blocks',
    description: 'Break and place of storage mod blocks: drawers, backpacks, Sophisticated and Tom\'s.',
    icon: Boxes,
    actions: blocksFor(STORAGE_MODS),
  },
  {
    id: 'ships',
    label: 'Ships',
    description: 'Ship lifecycle and movement, and the blocks broken or placed on a craft.',
    icon: Ship,
    actions: [
      'sable.ship_add', 'sable.ship_remove', 'sable.ship_move', 'sable.ship_loaded',
      'sable.ship_unload', 'sable.ship_rewind', 'sable.block_change',
      'simulated.ship_assemble', 'simulated.ship_disassemble', ...blocksFor(VEHICLE_MODS),
    ],
  },
  {
    id: 'land',
    label: 'Land and teams',
    description: 'Claims, force loads and team membership across FTB Chunks, FTB Teams and OPAC.',
    icon: MapPin,
    actions: [
      'ftbchunks.claim', 'ftbchunks.unclaim', 'ftbchunks.load', 'ftbchunks.unload',
      'ftbteams.ownership_transferred', 'ftbteams.properties_changed', 'ftbteams.player_changed',
      'ftbteams.add_ally', 'ftbteams.remove_ally', 'ftbteams.deleted',
      'openpac.claim_change', 'openpac.scope_change',
    ],
  },
  {
    id: 'trade',
    label: 'Trades',
    description: 'Villager trades and Cobblemon trade participation.',
    icon: Handshake,
    actions: ['merchant.trade', 'cobblemon.trade_participation'],
  },
  {
    id: 'integrity',
    label: 'Capture gaps',
    description: 'Intervals where capture failed. Evidence here is known to be incomplete.',
    icon: AlertTriangle,
    actions: ['logbook.gap'],
  },
];

/** Namespace of an action, used as the fallback grouping for adapters Argus has not met. */
export function actionNamespace(action: string): string {
  const index = action.indexOf('.');
  return index > 0 ? action.slice(0, index) : action;
}

const NAMESPACE_LABELS: Record<string, string> = {
  block: 'Vanilla', container: 'Vanilla', menu: 'Vanilla', merchant: 'Vanilla', inventory: 'Vanilla',
  item: 'Vanilla', player: 'Vanilla', create: 'Create', ae2: 'Applied Energistics 2',
  refinedstorage: 'Refined Storage', carryon: 'Carry On', ftbchunks: 'FTB Chunks', ftbteams: 'FTB Teams',
  openpac: 'Open Parties and Claims', waystones: 'Waystones', cobblemon: 'Cobblemon',
  logbook: 'Logbook', rewind: 'Logbook',
  ...Object.fromEntries(BLOCK_INTEGRATIONS.map(({ namespace, source }) => [namespace, source])),
};

function titleCase(value: string): string {
  return value.replace(/[._]/g, ' ').replace(/\b[a-z]/g, (character) => character.toUpperCase());
}

/** Never returns undefined: an unknown action still reads sensibly from its own name. */
export function actionMeta(action: string): LogbookActionMeta {
  const known = ACTION_INDEX.get(action);
  if (known) return known;
  const namespace = actionNamespace(action);
  return {
    action,
    label: titleCase(action.slice(namespace.length + 1) || action),
    verb: 'recorded',
    source: NAMESPACE_LABELS[namespace] ?? titleCase(namespace),
    category: 'Evidence integrity',
    tone: 'neutral',
    icon: CircleDot,
  };
}

// ── Outcomes ────────────────────────────────────────────────────────────────

export interface LogbookOutcomeMeta {
  label: string;
  tone: LogbookTone;
  meaning: string;
}

const OUTCOMES: Record<LogbookOutcome, LogbookOutcomeMeta> = {
  completed: { label: 'Completed', tone: 'success', meaning: 'The capture confirmed the operation succeeded.' },
  attempted: { label: 'Attempted', tone: 'warning', meaning: 'The action was started. Success was not confirmed.' },
  denied: { label: 'Denied', tone: 'danger', meaning: 'The server refused the action.' },
  cancelled: { label: 'Cancelled', tone: 'orange', meaning: 'The action was cancelled before it took effect.' },
  observed: { label: 'Observed', tone: 'neutral', meaning: 'A state change was seen. It does not establish who caused it or that a transfer happened.' },
  unknown: { label: 'Unknown', tone: 'neutral', meaning: 'The capture could not determine the outcome.' },
};

export function outcomeMeta(outcome: string): LogbookOutcomeMeta {
  return OUTCOMES[outcome as LogbookOutcome] ?? { label: titleCase(outcome), tone: 'neutral', meaning: 'Outcome reported by the capture.' };
}

// ── Actors ──────────────────────────────────────────────────────────────────

export interface LogbookActorMeta {
  label: string;
  tone: LogbookTone;
  icon: ComponentType<{ className?: string }>;
  meaning: string;
}

const ACTOR_KINDS: Record<LogbookActorKind, LogbookActorMeta> = {
  player: { label: 'Player', tone: 'accent', icon: User, meaning: 'A real player account.' },
  fake_player: { label: 'Fake player', tone: 'orange', icon: Bot, meaning: 'A machine acting under a fake player identity. Its owner is not the actor.' },
  automation: { label: 'Automation', tone: 'purple', icon: Cpu, meaning: 'An automated system, not a person.' },
  unknown: { label: 'Unattributed', tone: 'neutral', icon: Ban, meaning: 'No initiator was available to the capture. Ownership is separate metadata and does not name an actor.' },
};

export function actorMeta(kind: string): LogbookActorMeta {
  return ACTOR_KINDS[kind as LogbookActorKind] ?? ACTOR_KINDS.unknown;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Only a real UUID is safe to render as a player profile; anything else is a label. */
export function isPlayerUuid(actorId: string | null | undefined): boolean {
  return typeof actorId === 'string' && UUID_PATTERN.test(actorId);
}

// ── Metadata ────────────────────────────────────────────────────────────────

export type LogbookMetadataGroup = 'State change' | 'Attribution' | 'Evidence limits' | 'Context';

const METADATA_LABELS: Record<string, string> = {
  before_state: 'Before', after_state: 'After', before: 'Before', after: 'After',
  trade_correlation: 'Trade correlation', evidence_limit: 'Evidence limit',
  members_truncated: 'Members truncated', team_uuid: 'Team', owner_uuid: 'Owner at notification',
  chunk_x: 'Chunk X', chunk_z: 'Chunk Z', merchant_uuid: 'Merchant', merchant_type: 'Merchant type',
  offer_uses: 'Offer uses', payment_a: 'Payment slot 1', payment_b: 'Payment slot 2',
  requested_amount: 'Requested amount', unit_symbol: 'Unit', amount_per_unit: 'Amount per unit',
  position_qualifier: 'Position note', menu_type: 'Menu type', target_dimension: 'Target dimension',
  species: 'Species', pokemon_uuid: 'Pokemon', xp_cost: 'XP cost', loss_reason: 'Loss reason',
};

/** Groups metadata so a moderator reads the state change first and the caveats last. */
export function metadataGroup(key: string): LogbookMetadataGroup {
  if (/^(before|after)(_|$)/.test(key)) return 'State change';
  if (/(evidence_limit|truncated|degraded|skipped|loss|unsupported)/.test(key)) return 'Evidence limits';
  if (/(owner|team|actor|initiator|source|correlation|uuid)/.test(key)) return 'Attribution';
  return 'Context';
}

export function metadataLabel(key: string): string {
  return METADATA_LABELS[key] ?? titleCase(key);
}

export const METADATA_GROUP_ORDER: LogbookMetadataGroup[] = ['State change', 'Attribution', 'Context', 'Evidence limits'];

// ── Formatting ──────────────────────────────────────────────────────────────

/** Strips the namespace from a registry id for display: `minecraft:diamond` reads as `Diamond`. */
export function objectLabel(objectId: string): string {
  if (!objectId) return 'unknown object';
  const [, rest] = objectId.includes(':') ? [objectId.slice(0, objectId.indexOf(':')), objectId.slice(objectId.indexOf(':') + 1)] : ['', objectId];
  return titleCase(rest || objectId);
}

export function objectNamespace(objectId: string): string | null {
  const index = objectId.indexOf(':');
  return index > 0 ? objectId.slice(0, index) : null;
}

export function dimensionLabel(dimension: string): string {
  const named: Record<string, string> = {
    'minecraft:overworld': 'Overworld',
    'minecraft:the_nether': 'Nether',
    'minecraft:the_end': 'The End',
    'logbook:unknown': 'Unknown dimension',
  };
  return named[dimension] ?? objectLabel(dimension);
}

export function formatCoordinates(event: Pick<LogbookEvent, 'x' | 'y' | 'z'>): string {
  const round = (value: number) => (Number.isFinite(value) ? Math.round(value) : 0);
  return `${round(event.x)}, ${round(event.y)}, ${round(event.z)}`;
}

/** A Minecraft teleport command for the position, which is what staff paste next. */
export function teleportCommand(event: Pick<LogbookEvent, 'x' | 'y' | 'z' | 'dimension'>): string {
  const round = (value: number) => (Number.isFinite(value) ? Math.round(value) : 0);
  return `/execute in ${event.dimension} run tp @s ${round(event.x)} ${round(event.y)} ${round(event.z)}`;
}

/** Quantity is meaningful for transfers and meaningless for a claim or a menu access. */
export function hasMeaningfulQuantity(event: Pick<LogbookEvent, 'quantity' | 'action'>): boolean {
  if (!Number.isFinite(event.quantity) || event.quantity <= 0) return false;
  return !/^(menu\.|ftbchunks\.|ftbteams\.|openpac\.|player\.state|waystones\.|logbook\.|rewind\.)/.test(event.action)
    && !/\.ship_/.test(event.action);
}

/** `0` is the schema's label for unknown legacy time, not the Unix epoch. */
export function hasKnownTime(event: Pick<LogbookEvent, 'timestamp'>): boolean {
  return Number.isFinite(event.timestamp) && event.timestamp > 0;
}

export function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 100 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

export function formatCount(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString() : '0';
}

/** Relative age in the compact form the log rows use. Absolute time stays in the title attribute. */
export function relativeTime(timestamp: number, now = Date.now()): string {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 'unknown time';
  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`;
}

// ── Time windows ────────────────────────────────────────────────────────────

export interface LogbookWindowPreset {
  id: string;
  label: string;
  ms: number | null;
}

export const LOGBOOK_WINDOWS: LogbookWindowPreset[] = [
  { id: '1h', label: 'Last hour', ms: 60 * 60 * 1000 },
  { id: '6h', label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
  { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { id: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { id: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  { id: 'all', label: 'All retained', ms: null },
];

/** Bucket size that keeps a histogram readable without exceeding the 200 group ceiling. */
export function bucketForWindow(spanMs: number | null): 60000 | 3600000 | 86400000 {
  if (spanMs === null) return 86400000;
  if (spanMs <= 3 * 60 * 60 * 1000) return 60000;
  if (spanMs <= 8 * 24 * 60 * 60 * 1000) return 3600000;
  return 86400000;
}

/** Suggests a case id from today's date, matching the `case-YYYY-MMDD` shape in the API examples. */
export function suggestedCaseId(now = new Date()): string {
  const month = `${now.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${now.getUTCDate()}`.padStart(2, '0');
  return `case-${now.getUTCFullYear()}-${month}${day}`;
}

/** An event is worth flagging when the capture itself reported a problem. */
export function isEvidenceGap(event: Pick<LogbookEvent, 'action'>): boolean {
  return event.action === 'logbook.gap';
}
